import { useEffect, useRef } from "react";
import { Button } from "../../design/primitives";
import { useNavigationGuard } from "./NavigationGuard";
import css from "./studio.module.css";

/**
 * The unsaved-changes bar — FR-13, FR-14.
 *
 * Inline and maroon-bordered, directly under the breadcrumb row. Never a modal
 * and never a browser dialog: the same pattern as the delete confirmation
 * (§13.4), for the same reason — a question about the author's own work should
 * appear in the page holding that work, not in a chrome dialog that names no
 * record and offers no third option.
 *
 * The third option is the point. `KEEP EDITING` is first and is what a stray
 * click gets; the destructive one is last.
 */
export function GuardBar() {
  const { pending, registration, keepEditing, saveAndContinue, discardAndContinue } =
    useNavigationGuard();

  const bar = useRef<HTMLDivElement>(null);
  /*
   * Where focus was when the question was asked - usually the nav item that
   * triggered it. Restored on KEEP EDITING, so declining to leave puts the
   * keyboard back where it was rather than at the top of the document.
   */
  const returnFocusTo = useRef<HTMLElement | null>(null);

  /*
   * Move focus to the bar when it appears.
   *
   * It calls itself an `alertdialog`, and an alert nobody is sent to is just
   * markup. Without this a keyboard user presses a nav item, is silently
   * refused, and has no indication that anything was asked - the screen looks
   * unchanged from where their focus is.
   */
  useEffect(() => {
    if (!pending) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    bar.current?.focus();
  }, [pending]);

  if (!pending || !registration) return null;

  return (
    <div
      ref={bar}
      className={css.guardBar}
      role="alertdialog"
      aria-label="Unsaved changes"
      // Focusable programmatically but not a tab stop of its own: the buttons
      // inside are the actions, and stopping here twice would be noise.
      tabIndex={-1}
    >
      <div className={css.guardCopy}>
        <p className={css.guardHeading}>YOU HAVE UNSAVED CHANGES</p>
        <p className={css.guardBody}>
          Leaving now loses them. Save first, or throw them away deliberately.
        </p>
      </div>
      <div className={css.guardActions}>
        <Button
          variant="secondary"
          size="rail"
          onClick={() => {
            keepEditing();
            // Back to the control they pressed. Leaving focus on a bar that has
            // just been removed sends it to <body>.
            returnFocusTo.current?.focus();
          }}
        >
          KEEP EDITING
        </Button>
        <Button variant="secondary" size="rail" onClick={() => void saveAndContinue()}>
          {registration.saveLabel}
        </Button>
        <Button variant="destructive" size="rail" onClick={discardAndContinue}>
          DISCARD CHANGES
        </Button>
      </div>
    </div>
  );
}
